import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import sh from "bundles/common/utils/sh";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants";
import ConfirmModal from "bundles/common/components/ConfirmModal";
import ProjectHeaderFields from "./ProjectHeaderFields";
import ProjectFormFields from "./ProjectFormFields";
import ProjectMediaFields from "./ProjectMediaFields";
import { Trash2 } from "lucide-react";
import { RemoveScroll } from "react-remove-scroll";

const steps = [
  "project.title_and_description",
  "project.details",
  "project.cover_and_gallery",
];

const EditProjectModal = ({ project, closeModal, isNewProject, setFirstPartAndNew, isNew }) => {
  const isEdit = isNewProject == false;
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  // const currentCompany = currentRecruiter.company;
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const intl = useIntl();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const useStyles = makeStyles(() => ({
    modalContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      backgroundColor: "#fff",
      borderRadius: "16px",
      boxShadow: "0 20px 60px rgba(2, 51, 49, 0.18)",
      padding: isDesktop ? "40px 48px 24px 48px" : "24px",
      minWidth: isDesktop ? "1200px" : "unset",
      width: isDesktop ? "1200px" : "calc(100% - 32px) !important",
      maxHeight: "90vh",
      overflowY: "auto",
      height: isDesktop ? "580px" : "fit-content",
    },
    modalBox: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
    },
    headerBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "32px",
      gap: "32px"
    },
    inputBox: {
      marginBottom: isDesktop ? "24px" : "16px",
    },
    inputLabel: {
      "&.Mui-focused": {
        color: theme.palette.secondary.main,
      },
    },
  }));
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    setIsConfirmModalOpen(true);
  };

  const handleDeleteConfirmed = (e) => {
    e.preventDefault();
    sh.delete(`projects/${project.id}`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {
        authenticity_token: authenticityToken,
        project
      },
    })
      .then(() => {
        replaceCurrentRecruiter(dispatch, authenticityToken, () => {
          closeModal();
          enqueueSnackbar(intl.formatMessage({ id: "settings.save_success" }), {
            variant: "success",
          });
        });
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(intl.formatMessage({ id: "settings.save_failed" }), {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <RemoveScroll>
        <Box className={classes.modalContainer}>
          <form className={classes.modalBox}>
            <Box className={classes.headerBox}>
              <Box
                component="h2"
                sx={{
                  fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  fontSize: "1.25rem",
                  color: "hsl(178, 93%, 10%)",
                  letterSpacing: "-0.01em",
                  m: 0,
                }}
              >
                <FormattedMessage
                  id={isEdit ? "settings.edit_project" : "settings.add_project"}
                />
              </Box>
              {isEdit && (
                <IconButton
                  aria-label="delete"
                  disabled={loading}
                  onClick={handleDelete}
                >
                  <Trash2 size={20} color="hsl(0, 72%, 51%)" />
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Stepper activeStep={activeStep} sx={{ width: "80%" }}>
                {steps.map((labelId) => {
                  return (
                    <Step
                      key={labelId}
                      sx={{
                        "& .MuiStepLabel-root .Mui-active": {
                          color: "secondary.main",
                        },
                        "& .MuiStepLabel-root .Mui-completed": {
                          color: "secondary.main",
                        },
                      }}>
                      <StepLabel>
                        <FormattedMessage id={labelId} />
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
              {activeStep === 0 && (
                <ProjectHeaderFields
                  project={project}
                  handleNext={handleNext}
                  classes={classes}
                  currentRecruiter={currentRecruiter}
                  isNew={isNew}
                  setFirstPartAndNew={setFirstPartAndNew}
                />
              )}
              {activeStep === 1 && (
                <ProjectFormFields
                  project={project}
                  handleBack={handleBack}
                  handleNext={handleNext}
                  classes={classes}
                  currentRecruiter={currentRecruiter}
                />
              )}
              {activeStep === 2 && (
                <ProjectMediaFields
                  project={project}
                  handleBack={handleBack}
                  closeModal={closeModal}
                />
              )}
            </Box>
          </form>

          <ConfirmModal
            isOpen={isConfirmModalOpen}
            setIsOpen={setIsConfirmModalOpen}
            handleConfirm={handleDeleteConfirmed}
            labelId="delete_project_confirmation"
          />
        </Box>
      </RemoveScroll>
    </>
  );
};

export default EditProjectModal;
